/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

/* Functions */
import { post } from 'src/functions/fetch'
import { saveUrl } from 'src/functions/regex'

/* Options */
import config from 'src/config'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/index'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const withoutUndefined = <T extends Record<string, unknown>>(values: T): Partial<T> =>
  Object.entries(values).reduce<Partial<T>>((result, [key, value]) => {
    if (value !== undefined) result[key as keyof T] = value as T[keyof T]
    return result
  }, {})

const buildMode = () =>
  String(process.env.PRODUCT_BUILD_MODE || '').trim().toLowerCase()

const skipQuickSsrPaths = () =>
  process.env.CACHE === 'ssr' && buildMode() === 'quick'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweitere die Props um die SEO-Felder */
interface Props {
  mid: number
  title?: string
  products: tProductPreview[]
  count: number
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  description?: string
  canonicalUrl?: string
  stock?: boolean
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const FilterPage: React.FC<Props> = ({ 
  mid, title, products, count, description, metaTitle, metaDescription, metaKeywords, canonicalUrl, stock, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated
}: Props) => 
  <Filter {...{
    type: 'menu',
    mid,
    title,
    products,
    count,
    description,
    metaTitle,
    metaDescription,
    metaKeywords,
    canonicalUrl,
    stock,
    pageBuilder,
    sitePageBuilder,
    magazines,
    landingPageImageAiGenerated,
  }} />

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getStaticPaths: GetStaticPaths = async () => {
  const size = (() => {
    if (skipQuickSsrPaths()) return 0

    switch (process.env.CACHE) {
      case 'static': return config.cache_static_menues_size
      case 'ssr':    return config.cache_ssr_menues_size
      default:       return 1
    }
  })()

  if (size <= 0) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const data = await post({
    url: 'build/menuUrls/menues', 
    body: { 
      ...config.shop, 
      size,
      menu: process.env.NEXT_PUBLIC_MENU,
      token: process.env.BUILD_TOKEN,
    }, 
    local: true
  })

  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj, 'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj, 'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    console.error('Kann Menü-Pfade nicht laden!', data)
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const paths = data.map(({ id, path }: item) => {
    const mid = id.toString()
    const m_alias = saveUrl(path || mid)
    return {
      params: { mid, m_alias },
    }
  })

  return {
    paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

/* ------------------------------------------------------------------------------- */

interface IParams extends ParsedUrlQuery {
  mid: string
  m_alias: string
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { mid: midStr, m_alias } = params as IParams
  const mid = Number(midStr)
  const requestedCanonicalUrl = `m/${m_alias}/${mid}`

  let data: any
  try {
    data = await post({
      url: `products`,
      body: {
        ...config.shop,
        menuId: mid,
        filterTree: true,
      },
      local: true
    })
  } catch (err) {
    console.error('Kann Menü-Seite nicht laden!', { mid, canonicalUrl: requestedCanonicalUrl, err })
    throw err
  }

  if (!isFilterProductsResponse(data)) {
    console.error('Kann Menü-Seite nicht laden: ungültige Produkt-Antwort.', { mid, canonicalUrl: requestedCanonicalUrl, data })
    return { notFound: true }
  }

  const canonicalMenuAlias = saveUrl(data.menu?.path || m_alias || String(mid))
  const canonicalUrl = `m/${canonicalMenuAlias}/${mid}`

  if (canonicalMenuAlias && canonicalMenuAlias !== m_alias) {
    return {
      redirect: {
        destination: `/${canonicalUrl}`,
        permanent: true,
      },
    }
  }

  const siteSettings = await fetchSiteShopSettings({ local: true })
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings.landingPageImageAiGenerated,
  )

  return {
    props: {
      mid,
      ...withoutUndefined({
        title: data.menu?.titel,
        metaTitle: data.menu?.metaTitle,
        metaDescription: data.menu?.metaDescription,
        metaKeywords: data.menu?.metaKeywords,
        description: data.menu?.description,
        stock: data.menu?.stock,
      }),
      ...(data.menu?.pageBuilder ? { pageBuilder: data.menu.pageBuilder } : {}),
      ...(data.menu?.magazines ? { magazines: data.menu.magazines } : {}),
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(landingPageImageAiGenerated ? { landingPageImageAiGenerated } : {}),
      count: data?.filterProducts.count || 0,
      products: data?.filterProducts.products || [],
      canonicalUrl,
    },
    revalidate: 60,
  }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
